import {
    BaseEdge, EdgeLabelRenderer, EdgeProps, useReactFlow,
} from "@xyflow/react";
import React, { useCallback, useMemo } from "react";
import { RiAddFill } from "@remixicon/react";
import classNames from "classnames";
import Button from "../../../theme/button/Button";
import Styles from "./CurvedEdge.module.scss";
import { WorkflowStepGroup } from "../../../../__generated__/api";
import { useWorkflow, WorkflowStepGroupWithNodeData } from "../../providers/WorkflowProvider";
import { SidebarContent, useWorkflowModal } from "../../providers/WorkflowModalProvider";

export default function CurvedEdge(props: EdgeProps) {
    const {
        id, sourceX, sourceY, targetX, targetY, data,
    } = props;

    const { getNodesBounds, getNode } = useReactFlow();
    const { setCurrentStep, addTemporaryNode, temporaryNodeId } = useWorkflow();
    const { setIsSidebarOpen, setSidebarContent } = useWorkflowModal();

    const { y, height } = getNodesBounds([data?.targetId as string]);

    const edgePath = `
    M ${sourceX} ${sourceY} 
    L ${sourceX} ${y}
    Q ${sourceX} ${y + height / 2} ${targetX} ${targetY}
    `;

    const isHovered = useMemo(() => !!data?.hovered, [data]);

    const midPoint = useMemo(() => ({
        x: sourceX - 18,
        y: y + height / 2 - 18,
    }), [sourceX, y, height]);

    const handleAddNewNode = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();

        const sourceNode = getNode(data?.sourceId as string);
        const targetNode = getNode(data?.targetId as string);

        if (!sourceNode || !targetNode) {
            return;
        }

        const targetNodeIndex = (sourceNode?.data as WorkflowStepGroup)?.children
            ?.findIndex(child => child.id === targetNode?.id);

        // Slot in after the target node
        const newNodeIndex = targetNodeIndex !== undefined && targetNodeIndex >= 0 ? targetNodeIndex + 1 : 0;

        addTemporaryNode({ nodeId: sourceNode.id, index: newNodeIndex });
        setCurrentStep(sourceNode?.data as WorkflowStepGroupWithNodeData);
        setIsSidebarOpen(true);
        setSidebarContent(SidebarContent.ACTIONS);
    }, [data?.sourceId, data?.targetId, getNode]);

    return (
        <>
            <BaseEdge
                id={id}
                path={edgePath}
            />
            <EdgeLabelRenderer>
                { isHovered && !temporaryNodeId && (
                    <Button
                        style={{
                            top: midPoint.y,
                            left: midPoint.x,
                        }}
                        className={classNames(Styles["add-new-node-button"], "nopan nodrag")}
                        size="large"
                        variant="none"
                        onClick={handleAddNewNode}
                        prefixIcon={<RiAddFill className={Styles["add-new-node-button-icon"]} />}
                    />
                )}
            </EdgeLabelRenderer>
        </>
    );
}
